import { useContext, useEffect, useRef, useState } from 'react';
import { Heading, Text, useToast, Box, Flex } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../../constants/environment';
import SessionContext from '../../contexts/SessionContext';
import { AgenticStepLoader, type AgenticStep } from '../AgenticStepLoader';
import {
	SupplierMappingUI,
	type PtSupplier,
	type OpsSupplier,
	type ExistingSupplier,
	type SupplierMapping,
	type SupplierRowMapping,
} from './SupplierMappingUI';
import { StepFooter } from './StepFooter';

const AGENTIC_STEP_OPS: AgenticStep = {
	label: 'Loading OPS suppliers',
	description: 'Reading the OPS supplier network pulled earlier',
};

const AGENTIC_STEP_PT: AgenticStep = {
	label: 'Loading PartsTrader suppliers',
	description: 'Reading the PartsTrader supplier network pulled earlier',
};

const AGENTIC_STEP_RECONCILE: AgenticStep = {
	label: 'Matching supplier network',
	description: 'Reconciling suppliers against your existing network',
};

interface ReconcileResult {
	source: 'opstrax' | 'partstrader';
	externalId: number;
	name: string;
	action: 'id-matched' | 'auto-merged' | 'unresolved';
	supplierId?: string;
	supplierTitle?: string;
	score?: number;
	topCandidates?: Array<{ supplierId: string; title: string; score: number }>;
}

interface ReconcileResponse {
	ready?: boolean;
	sources?: string[];
	total?: number;
	idMatched?: number;
	autoMerged?: number;
	unmapped?: number;
	crossSourceMerged?: Array<{ ptExternalId: number; opsExternalId: number; supplierId: string; score: number }>;
	results?: ReconcileResult[];
	unresolved?: Array<{ source: 'opstrax' | 'partstrader'; externalId: number; name: string; topCandidates: Array<{ supplierId: string; title: string; score: number }> }>;
	existingSuppliers?: ExistingSupplier[];
	ptSuppliers?: PtSupplier[];
	opsSuppliers?: OpsSupplier[];
}

interface SupplierNetworkStepProps {
	organizationId: string;
	onComplete: () => void;
	onSkip: () => void;
	onBack?: () => void;
	usesPt?: boolean;
}


const uniqueNumbers = (values: number[]): number[] => Array.from(new Set(values));

export const SupplierNetworkStep = ({
	organizationId,
	onComplete,
	onSkip,
	onBack,
	usesPt = true,
}: SupplierNetworkStepProps) => {
	const sessionContext = useContext(SessionContext);
	const toast = useToast();
	const hasKickedOff = useRef(false);

	const [ptSuppliers, setPtSuppliers] = useState<PtSupplier[]>([]);
	const [opsSuppliers, setOpsSuppliers] = useState<OpsSupplier[]>([]);
	const [existingSuppliersList, setExistingSuppliersList] = useState<ExistingSupplier[]>([]);
	const [hasOpsData, setHasOpsData] = useState<boolean | null>(null);
	const [hasPtData, setHasPtData] = useState<boolean | null>(null);
	const [rowMappings, setRowMappings] = useState<Record<string, SupplierRowMapping>>({});
	const [phase, setPhase] = useState<'waiting' | 'reconciling' | 'mapping' | 'empty'>('waiting');
	const [ptOnly, setPtOnly] = useState(false);
	const [statusPollTick, setStatusPollTick] = useState(0);

	const reconcile = useMutation({
		mutationFn: async (input: { organizationId: string; sources?: Array<'ops' | 'pt'> }) => {
			const { data } = await axios.post<ReconcileResponse>(
				`${API_URL}/onboarding/reconcile-suppliers`,
				input,
				{ headers: { Authorization: sessionContext?.sessionId ?? '' } }
			);
			return data;
		},
	});

	const confirmMappings = useMutation({
		mutationFn: async (input: { organizationId: string; mappings: Array<{
			ptSupplierId?: number;
			ptSupplierName?: string;
			ptPartTypes?: string[];
			opsDealerIds?: number[];
			opsSupplierNames?: string[];
			existingId?: string;
		}> }) => {
			const { data } = await axios.post(`${API_URL}/onboarding/confirm-supplier-mappings`, input, {
				headers: { Authorization: sessionContext?.sessionId ?? '' },
			});
			return data;
		},
	});

	// Build the agentic steps based on which sources the user actually onboarded with
	const agenticSteps: AgenticStep[] = [
		...(hasOpsData ? [AGENTIC_STEP_OPS] : []),
		...(hasPtData ? [AGENTIC_STEP_PT] : []),
		AGENTIC_STEP_RECONCILE,
	];

	const getCurrentStep = () => {
		if (phase === 'reconciling') return agenticSteps.length - 1;
		// During the "waiting" phase, walk through the data-loading steps sequentially
		if (hasOpsData === null && hasPtData === null) return 0;
		if (hasOpsData && !hasPtData) return 0; // only ops loaded
		if (hasPtData && !hasOpsData) return 0; // only pt loaded
		return 1; // both — show pt step while reconcile kicks off
	};

	// Supplier data is bundled with the earlier pull-pt-locations / pull-ops-trax-locations
	// workflows. Before reconciling, detect which sources the user actually used so the
	// agentic step chain reflects reality — and skip the step entirely if no source
	// produced (or will produce) data. CCC has no supplier crawl in this pipeline, so an
	// onboarding with only CCC credentials lands here with nothing to reconcile.
	useEffect(() => {
		if (hasKickedOff.current || !organizationId) return;
		hasKickedOff.current = true;
		if (!usesPt) setPtOnly(false);

		const headers = { Authorization: sessionContext?.sessionId ?? '' };

		// `null` means no workflow row exists (the user never provided creds for that
		// platform); `'failed'` means the workflow ran but produced no usable supplier data.
		// Either way the source is "useless" for reconciliation purposes.
		const fetchStatus = async (target: 'partstrader' | 'opstrax'): Promise<string | null> => {
			try {
				const { data } = await axios.get<{ status: string | null; suppliers?: unknown[] | null }>(
					`${API_URL}/onboarding/pull-status`,
					{ params: { organizationId, target }, headers }
				);
				return data?.status ?? null;
			} catch {
				return null;
			}
		};

		const isUseless = (status: string | null) => status === null || status === 'failed';
		const isStillRunning = (status: string | null) => status === 'pending' || status === 'in-progress';

		void (async () => {
			const [opsStatus, ptStatus] = await Promise.all([
				fetchStatus('opstrax'),
				usesPt ? fetchStatus('partstrader') : Promise.resolve(null),
			]);

			// Nothing was attempted (or both attempts failed) — auto-skip. Common case:
			// onboarding with only CCC credentials and no PT/OPS provided.
			if (isUseless(opsStatus) && isUseless(ptStatus)) {
				onComplete();
				return;
			}

			// If at least one relevant supplier source is still running, stay on the loader
			// and re-check shortly. Do not call reconcile yet; the backend will return
			// ready:false and strand the user in this step.
			if (isStillRunning(opsStatus) || isStillRunning(ptStatus)) {
				hasKickedOff.current = false;
				setHasOpsData(opsStatus === 'completed');
				setHasPtData(ptStatus === 'completed');
				setTimeout(() => setStatusPollTick((tick) => tick + 1), 3000);
				return;
			}

			const opsOk = opsStatus === 'completed';
			const ptOk = ptStatus === 'completed';
			setHasOpsData(opsOk);
			setHasPtData(ptOk);
			if (!opsOk && ptOk) setPtOnly(true);
			void handleReconcile();
		})();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organizationId, statusPollTick]);

	const handleReconcile = async () => {
		setPhase('reconciling');
		try {
			const activeSources: Array<'ops' | 'pt'> = [];
			if (!ptOnly) activeSources.push('ops');
			if (usesPt) activeSources.push('pt');

			const result = await reconcile.mutateAsync({
				organizationId,
				sources: activeSources,
			});

			if (result.ready === false) {
				toast({
					title: 'Suppliers not ready yet',
					description: 'Still pulling supplier data. Please wait and try again.',
					status: 'warning',
					duration: 4000,
					isClosable: true,
				});
				setPhase('waiting');
				return;
			}

			const pt = result.ptSuppliers ?? [];
			const ops = result.opsSuppliers ?? [];
			const existing = result.existingSuppliers ?? [];
			setPtSuppliers(pt);
			setOpsSuppliers(ops);
			setExistingSuppliersList(existing);
			if (ops.length === 0) setPtOnly(true);

			const results = result.results ?? [];

			// Build rows from results — keyed by existingId when matched, or by source-externalId when unmapped
			const initialRows: Record<string, SupplierRowMapping> = {};

			for (const r of results) {
				const existingSupplier = r.supplierId ? existing.find(e => e._id === r.supplierId) : undefined;
				const key = r.supplierId ? `existing-${r.supplierId}` : `${r.source}-${r.externalId}`;
				const current = initialRows[key];

				const ptIds = r.source === 'partstrader'
					? uniqueNumbers([...(current?.ptIds ?? []), r.externalId])
					: (current?.ptIds ?? []);

				const opsDealerIds = r.source === 'opstrax'
					? uniqueNumbers([...(current?.opsDealerIds ?? []), r.externalId])
					: (current?.opsDealerIds ?? []);

				initialRows[key] = {
					enabled: current?.enabled ?? true,
					ptIds,
					opsDealerIds,
					existingId: r.supplierId ?? current?.existingId,
					existingHasOpsId: current?.existingHasOpsId ?? ((existingSupplier?.opsId?.length ?? 0) > 0),
				};
			}

			if (Object.keys(initialRows).length === 0) {
				setPhase('empty');
				return;
			}
			setRowMappings(initialRows);
			setPhase('mapping');
		} catch (err) {
			console.error('[SupplierNetworkStep] reconcile error:', err);
			toast({
				title: 'Failed to reconcile suppliers',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
			setPhase('waiting');
		}
	};

	const handleConfirm = async (mappings: SupplierMapping[]) => {
		const ptMap = new Map(ptSuppliers.map((s) => [s.id, s]));
		const opsMap = new Map(opsSuppliers.map((s) => [s.dealerId, s]));

		const payload = mappings.map((m) => ({
			ptSupplierId: m.ptSupplierId,
			ptSupplierName: m.ptSupplierId ? ptMap.get(m.ptSupplierId)?.name : undefined,
			ptPartTypes: m.ptSupplierId ? ptMap.get(m.ptSupplierId)?.partTypes : undefined,
			opsDealerIds: m.opsDealerIds,
			opsSupplierNames: m.opsDealerIds
				?.map((id) => opsMap.get(id)?.name)
				.filter((name): name is string => !!name),
			existingId: m.existingId,
		}));

		try {
			if (payload.length === 0) {
				toast({
					title: 'No supplier mappings to save',
					description: 'All discovered suppliers already exist in your network.',
					status: 'info',
					duration: 3000,
					isClosable: true,
				});
				onComplete();
				return;
			}
			await confirmMappings.mutateAsync({ organizationId, mappings: payload });
			toast({
				title: 'Supplier network saved',
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			onComplete();
		} catch {
			toast({
				title: 'Failed to save supplier mappings',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		}
	};

	if (phase === 'waiting' || phase === 'reconciling') {
		return (
			<Flex align="center" justify="center" width="100%" flex={1} minH="400px">
				<AgenticStepLoader
					title="Reconciling supplier network"
					subtitle="Matching your marketplace suppliers against the existing supplier collection."
					steps={agenticSteps}
					currentStep={getCurrentStep()}
				/>
			</Flex>
		);
	}

	if (phase === 'empty') {
		return (
			<Flex direction="column" flex={1} width="100%" minH={0}>
				<Box flex={1} minH={0} overflowY="auto" pt={4}>
					<Heading size="md" mb={2}>Supplier Network</Heading>
					<Text color="gray.500" fontSize="sm">No suppliers discovered. You can skip this step.</Text>
				</Box>
				<StepFooter
					back={onBack ? { onClick: onBack } : undefined}
					secondaryActions={[{ label: 'Skip', onClick: onSkip, variant: 'outline' }]}
				/>
			</Flex>
		);
	}

	return (
		<SupplierMappingUI
			ptSuppliers={ptSuppliers}
			opsSuppliers={opsSuppliers}
			existingSuppliers={existingSuppliersList}
			ptOnly={ptOnly}
			usesPt={usesPt}
			onConfirm={handleConfirm}
			onSkip={onSkip}
			isConfirmLoading={confirmMappings.isPending}
			rowMappings={rowMappings}
			onToggleRow={(key) => {
				setRowMappings((prev) => ({
					...prev,
					[key]: { ...prev[key], enabled: !prev[key].enabled },
				}));
			}}
			onAddPt={(key, ptId) => {
				setRowMappings((prev) => ({
					...prev,
					[key]: { ...prev[key], ptIds: [...prev[key].ptIds, ptId] },
				}));
			}}
			onRemovePt={(key, ptId) => {
				setRowMappings((prev) => ({
					...prev,
					[key]: { ...prev[key], ptIds: prev[key].ptIds.filter((id) => id !== ptId) },
				}));
			}}
			onAddOps={(key, dealerId) => {
				setRowMappings((prev) => ({
					...prev,
					[key]: { ...prev[key], opsDealerIds: [...prev[key].opsDealerIds, dealerId] },
				}));
			}}
			onRemoveOps={(key, dealerId) => {
				setRowMappings((prev) => ({
					...prev,
					[key]: { ...prev[key], opsDealerIds: prev[key].opsDealerIds.filter((id) => id !== dealerId) },
				}));
			}}
		/>
	);
};
